<template>
	<div>
		<v-text-field
			label="Scene Name"
			:value="form.name"
			:readonly="!editMode"
			:rules="[rules.required, rules.nameUnique, rules.nameValid]"
			@input="updateField('name', $event)"
		/>
		<h4>Device</h4>
		<v-select
			label="Thermostat"
			no-data-text="No thermostats found for this room."
			item-value="path"
			:item-text="thermostatItemName"
			:items="thermostats"
			:value="form.thermostat"
			:readonly="!editMode"
			:append-icon="editMode ? 'fa-caret-down' : ''"
			:rules="[rules.required]"
			@change="updateField('thermostat', $event)"
		/>

		<h4>Settings</h4>
		<v-select
			label="Mode"
			:items="modeOptions"
			:value="form.mode"
			:readonly="!editMode"
			:append-icon="editMode ? 'fa-caret-down' : ''"
			:rules="[rules.required]"
			@change="updateField('mode', $event)"
		/>
		<v-text-field
			v-if="[null, 'magic'].includes(form.mode)"
			label="Temperature"
			:value="form.temp"
			:readonly="!editMode"
			:rules="[rules.required]"
			:disabled="!form.mode"
			type="number"
			@input="updateField('temp', $event)"
		/>
		<template v-else>
			<v-text-field
				v-if="form.mode !== 'heat'"
				label="Temperature (cool)"
				:value="form.cool"
				:readonly="!editMode"
				:rules="[rules.required]"
				:disabled="!form.mode"
				type="number"
				@input="updateField('cool', $event)"
			/>
			<v-text-field
				v-if="form.mode !== 'cool'"
				label="Temperature (heat)"
				:value="form.heat"
				:readonly="!editMode"
				:rules="[rules.required]"
				:disabled="!form.mode"
				type="number"
				@input="updateField('heat', $event)"
			/>
		</template>

		<h4>When</h4>
		<shared-time-field
			label="Time"
			:value="form.time"
			:readonly="!editMode"
			:rules="[rules.required]"
			@input="updateField('time', $event)"
		/>
	</div>
</template>

<script>
import _ from "lodash";
import parse from "@/utils/parse";
import { DateTime } from "luxon";
import { mapGetters } from "vuex";

const initData = () => ({
	timeMenu: false,
	updatedFields: [],
	form: {
		name: "",
		thermostat: null,
		mode: null,
		temp: "",
		cool: "",
		heat: "",
		time: "",
	},
});

export default {
	props: {
		scene: Object,
	},

	data: initData,
	computed: {
		...mapGetters("scenes/temperature", ["modeOptions"]),
		...mapGetters("controller/nodes", ["thermostats"]),
		...mapGetters("scenes", ["scenes", "sceneNodes"]),

		time: {
			get() {
				return this.form.time;
			},
			set(time) {
				this.updateField("time", time);
			},
		},

		amPmTime() {
			const time = parse.time(this.time);
			if (time) return time.toLocaleString(DateTime.TIME_SIMPLE);
			return this.time;
		},

		rules() {
			return {
				required: (v) => !!v || "Field cannot be empty!",
				nameUnique: () =>
					!this.editMode ||
					_.get(this.scene, "name") === this.form.name ||
					!this.scenes.map((s) => s.name).includes(this.form.name) ||
					"A scene with this name already exists!",
				nameValid: (v) => !v.match(/\//) || "Invalid character!",
			};
		},

		editMode() {
			return this.$store.getters["scenes/editMode"] || !this.scene;
		},
	},

	watch: {
		scene(val) {
			if (val && !this.editMode) this.parseScene(val);
		},

		form: {
			deep: true,
			handler(form) {
				this.$emit("update:form", form);
			},
		},

		updatedFields: {
			deep: true,
			handler(fields) {
				this.$emit("update:fields", fields);
			},
		},
	},

	created() {
		if (this.scene) this.parseScene(this.scene);
	},

	methods: {
		updateField(field, value) {
			this.form[field] = value;
			if (!this.updatedFields.includes(field)) this.updatedFields.push(field);
		},

		thermostatItemName({ path, name }) {
			const area = path.split("/").reverse()[1];
			return `${area} ${name}`;
		},

		parseScene(scene) {
			this.$emit("reset");
			const { commands, timeConditions } = this.sceneNodes;
			const modeNode = commands.find(
				(command) => command.target.name === "setMode"
			);
			const smartNode = commands.find(
				(command) => command.target.name === "enableSmartMode"
			);
			if (modeNode) {
				this.form.thermostat = _.get(
					modeNode,
					"target.parentDevice.path",
					null
				);
				this.form.mode = _.get(modeNode, "args.mode", null);
				const tempNodes = commands.filter((command) =>
					_.get(command, "args.temp")
				);
				this.form.temp = _.get(tempNodes[0], "args.temp", "");
				this.form.heat = _.get(
					tempNodes.find((node) => node.target.name === "setHeat"),
					"args.temp",
					""
				);
				this.form.cool = _.get(
					tempNodes.find((node) => node.target.name === "setCool"),
					"args.temp",
					""
				);
			} else if (smartNode) {
				this.form.thermostat = _.get(smartNode, "args.thermostatPath");
				this.form.mode = "magic";
				this.form.temp = _.get(smartNode, "args.temperature");
			}

			const timeNode = timeConditions.find(
				(condition) => condition.operator === "=="
			);
			this.form.time = _.get(timeNode, "value", "");

			this.form.name = _.get(scene, "name", "");

			this.checkExtraNodes();
		},

		checkExtraNodes() {
			const sceneLength = _.filter([
				this.form.time,
				this.form.mode,
				this.form.mode !== "magic",
				this.form.mode === "auto",
				this.form.temp,
			]).length;
			const hasExtra =
				_.reduce(this.sceneNodes, (length, nodes) => length + nodes.length, 0) >
				sceneLength;
			this.$emit("update:extra", hasExtra);
		},

		reset() {
			if (this.scene) this.parseScene(this.scene);
			else Object.assign(this, initData());
		},
	},
};
</script>

<style lang="scss" scoped>
</style>
